import { useEffect, useState } from 'react'
import { client, type PhotoInfo } from '../lib/api'
import { MAX_PROFILE_PHOTOS } from '../../shared/onboarding'
import {
  CONNECTION_INTENT_LABEL,
  CONNECTION_INTENT_OPTIONS,
  GENDER_IDENTITY_LABEL,
  GENDER_IDENTITY_OPTIONS,
  LIFESTYLE_TAG_SUGGESTIONS,
  OUTREACH_LABEL,
  OUTREACH_OPTIONS,
  outreachFromDynamic,
  type ConnectionIntent,
  type GenderIdentity,
  type OutreachPreference,
} from '../lib/ui'
import { ProfilePhotoGrid } from './ProfilePhotoGrid'
import {
  SocialLinksEditor,
  SocialLinksRow,
  emptySocialLinksForm,
  socialLinksFormFromApi,
  socialLinksInputFromForm,
} from './SocialLinks'
import { buildSocialLink, SOCIAL_PLATFORM_ORDER, type SocialLink } from '../../shared/social-links'
import type { MeUser } from './Settings'

function userOutreach(user: MeUser): OutreachPreference {
  return user.outreach_preference ?? outreachFromDynamic(user.dynamic_role ?? 'flexible')
}

function userIntent(user: MeUser): ConnectionIntent {
  return user.connection_intent ?? 'exploring'
}

function userAge(user: MeUser): number | null {
  if (!user.birth_year) return null
  return new Date().getFullYear() - user.birth_year
}

export function Profile({
  user,
  onSaved,
  notify,
}: {
  user: MeUser
  onSaved: () => void
  notify: (msg: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [displayName, setDisplayName] = useState(user.display_name)
  const [heading, setHeading] = useState(user.heading ?? '')
  const [bio, setBio] = useState(user.bio ?? '')
  const [city, setCity] = useState(user.city ?? '')
  const [outreach, setOutreach] = useState<OutreachPreference>(userOutreach(user))
  const [intent, setIntent] = useState<ConnectionIntent>(userIntent(user))
  const [lifestyleTags, setLifestyleTags] = useState<string[]>([])
  const [socialLinksForm, setSocialLinksForm] = useState(emptySocialLinksForm)
  const [gender, setGender] = useState<GenderIdentity | ''>((user.gender as GenderIdentity) ?? '')
  const [photos, setPhotos] = useState<PhotoInfo[]>([])
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const reload = async () => {
    const d = await client.profileDetail(user.id)
    setPhotos(d.photos)
    setLifestyleTags(d.tags)
    setSocialLinksForm(socialLinksFormFromApi(d.socialLinks ?? []))
    return d
  }

  useEffect(() => {
    void reload().catch(() => undefined)
  }, [user.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setDisplayName(user.display_name)
    setHeading(user.heading ?? '')
    setBio(user.bio ?? '')
    setCity(user.city ?? '')
    setOutreach(userOutreach(user))
    setIntent(userIntent(user))
    setGender((user.gender as GenderIdentity) ?? '')
  }, [user])

  const publicPhotos = [...photos.filter((p) => p.kind === 'public')].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  )
  const approvedCount = publicPhotos.filter((p) => p.moderationStatus === 'approved').length
  const photoUrl = publicPhotos[0]?.urlPath ?? null
  const age = userAge(user)

  const hubSocialLinks: SocialLink[] = SOCIAL_PLATFORM_ORDER.map((platform) => {
    const handle = socialLinksForm[platform]?.trim()
    if (!handle) return null
    return buildSocialLink(platform, handle)
  }).filter((l): l is SocialLink => !!l)

  const toggleTag = (tag: string) => {
    setLifestyleTags((t) => (t.includes(tag) ? t.filter((x) => x !== tag) : [...t, tag]))
  }

  const upload = async (file: File) => {
    setUploadingPhoto(true)
    try {
      const res = await client.uploadPhoto(file, 'public')
      notify(res.message ?? (res.moderation === 'approved' ? 'Photo approved' : 'Photo uploaded — pending review'))
      await reload()
      onSaved()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setUploadingPhoto(false)
    }
  }

  const replacePhoto = async (photoId: string, file: File) => {
    setUploadingPhoto(true)
    try {
      const res = await client.replacePhoto(photoId, file)
      notify(res.message ?? (res.moderation === 'approved' ? 'Photo approved' : 'Photo updated — pending review'))
      await reload()
      onSaved()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setUploadingPhoto(false)
    }
  }

  const reorderPhotos = async (photoIds: string[]) => {
    await client.reorderPhotos(photoIds)
    await reload()
    onSaved()
  }

  const deletePhoto = async (photoId: string) => {
    try {
      await client.deletePhoto(photoId)
      notify('Photo deleted')
      await reload()
      onSaved()
    } catch (e) {
      notify((e as Error).message)
      throw e
    }
  }

  const cancelEdit = async () => {
    setEditing(false)
    setDisplayName(user.display_name)
    setHeading(user.heading ?? '')
    setBio(user.bio ?? '')
    setCity(user.city ?? '')
    setOutreach(userOutreach(user))
    setIntent(userIntent(user))
    setGender((user.gender as GenderIdentity) ?? '')
    try {
      await reload()
    } catch {
      /* ignore */
    }
  }

  const saveProfile = async () => {
    setSaving(true)
    try {
      await client.updateProfile({
        displayName,
        heading,
        bio,
        city,
        gender: gender || undefined,
        outreachPreference: outreach,
        connectionIntent: intent,
        lifestyleTags,
        socialLinks: socialLinksInputFromForm(socialLinksForm),
      })
      notify('Profile saved')
      setEditing(false)
      onSaved()
      await reload()
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`panel-page profile-page${editing ? ' profile-page--editing' : ''}`}>
      <div className="profile-page-top">
        <div>
          <span className="eyebrow">{editing ? 'Editing' : 'Your profile'}</span>
          <h1>{editing ? 'Edit profile' : displayName}</h1>
          <p className="page-sub">
            {editing ? 'Every field below is unlocked — change what you need, then save.' : 'This is how others see you.'}
          </p>
        </div>
        {!editing ? (
          <button type="button" className="button primary" onClick={() => setEditing(true)}>
            Edit profile
          </button>
        ) : (
          <button type="button" className="button secondary" onClick={() => void cancelEdit()} disabled={saving}>
            Cancel
          </button>
        )}
      </div>

      {editing ? (
        <>
          <p className="page-sub">
            First photo is your profile picture. Tap a photo to crop, reorder, or delete — up to {MAX_PROFILE_PHOTOS}.
          </p>
          <ProfilePhotoGrid
            photos={publicPhotos}
            maxPhotos={MAX_PROFILE_PHOTOS}
            busy={uploadingPhoto}
            manageable
            uploadLabel="Add photo"
            hint={`${approvedCount} approved · ${publicPhotos.length}/${MAX_PROFILE_PHOTOS}`}
            onUpload={upload}
            onReplace={replacePhoto}
            onReorder={reorderPhotos}
            onDelete={deletePhoto}
            onNotify={notify}
          />
        </>
      ) : (
        <div className="own-profile">
          <div className="own-profile-photo">
            {photoUrl ? (
              <img src={photoUrl} alt="" />
            ) : (
              <div className="own-profile-avatar-fallback own-avatar" style={{ width: 112, height: 112, fontSize: 40 }}>
                {displayName.slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
          <div className="own-profile-copy">
            <h2>
              {displayName}
              {age != null ? <em>, {age}</em> : null}
            </h2>
            <p className="own-profile-city">{city || 'Add your city'}</p>
            {user.verification_status === 'verified' && <span className="verified-badge">✓ Verified</span>}
          </div>
        </div>
      )}

      <div className="profile-fields">
        {editing ? (
          <>
            <div className="field">
              <label>Display name</label>
              <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div className="field">
              <label>City</label>
              <input className="input" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div className="field">
              <label>I am a</label>
              <div className="segmented">
                {GENDER_IDENTITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={gender === opt.value ? 'selected' : ''}
                    onClick={() => setGender(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {gender && <p className="field-hint">Shown as {GENDER_IDENTITY_LABEL[gender]}</p>}
            </div>
          </>
        ) : null}

        <div className="field">
          <label>Heading</label>
          {editing ? (
            <input className="input" value={heading} onChange={(e) => setHeading(e.target.value)} />
          ) : heading ? (
            <p className="profile-sheet-heading">{heading}</p>
          ) : (
            <p className="page-sub">No heading yet</p>
          )}
        </div>

        <div className="field">
          <label>Bio</label>
          {editing ? (
            <>
              <textarea
                className="input"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Your vibe, what you create, what you're open to…"
              />
              <p className="field-hint">No links or phone numbers — use Social links.</p>
            </>
          ) : bio ? (
            <p className="profile-bio-preview">{bio}</p>
          ) : (
            <p className="page-sub">No bio yet</p>
          )}
        </div>

        <div className="field">
          <label>Social links</label>
          {editing ? (
            <SocialLinksEditor value={socialLinksForm} onChange={setSocialLinksForm} />
          ) : hubSocialLinks.length ? (
            <SocialLinksRow links={hubSocialLinks} />
          ) : (
            <p className="page-sub">No social links yet</p>
          )}
        </div>

        <div className="profile-meta-grid">
          <div className="field">
            <label>Outreach</label>
            {editing ? (
              <div className="segmented">
                {OUTREACH_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={outreach === opt.value ? 'selected' : ''}
                    onClick={() => setOutreach(opt.value)}
                  >
                    {OUTREACH_LABEL[opt.value]}
                  </button>
                ))}
              </div>
            ) : (
              <strong>{OUTREACH_LABEL[outreach]}</strong>
            )}
          </div>
          <div className="field">
            <label>Here for</label>
            {editing ? (
              <div className="segmented">
                {CONNECTION_INTENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={intent === opt.value ? 'selected' : ''}
                    onClick={() => setIntent(opt.value)}
                    title={opt.blurb}
                  >
                    {CONNECTION_INTENT_LABEL[opt.value]}
                  </button>
                ))}
              </div>
            ) : (
              <strong>{CONNECTION_INTENT_LABEL[intent]}</strong>
            )}
          </div>
        </div>

        <div className="field">
          <label>Interests & vibe</label>
          {editing ? (
            <div className="chip-row">
              {LIFESTYLE_TAG_SUGGESTIONS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className={`chip ${lifestyleTags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          ) : lifestyleTags.length > 0 ? (
            <div className="detail-pills">
              {lifestyleTags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          ) : (
            <p className="page-sub">No tags yet</p>
          )}
        </div>

        {!editing && publicPhotos.length > 1 && (
          <div className="profile-preview-photos">
            {publicPhotos.slice(0, 6).map((p) =>
              p.urlPath ? <img key={p.id} src={p.urlPath} alt="" loading="lazy" /> : null,
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="profile-save-bar">
          <button type="button" className="button primary" onClick={() => void saveProfile()} disabled={saving}>
            {saving ? 'Saving…' : 'Save profile'}
          </button>
          <button type="button" className="button secondary" onClick={() => void cancelEdit()} disabled={saving}>
            Cancel
          </button>
        </div>
      )}
    </div>
  )
}
